import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';

// ── Stable sub-components (defined outside to avoid re-creation on every render) ──

function Section({ title, icon, color, children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
      <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center">
        <div className={`w-8 h-8 ${color} rounded-lg flex items-center justify-center mr-2`}>
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} /></svg>
        </div>
        {title}
      </h3>
      {children}
    </div>
  );
}

function InputField({ label, name: fieldName, required, type = 'text', placeholder, disabled, className = '', hint, value, onChange, ...rest }) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label} {required && <span className="text-red-400">*</span>}</label>
      <input name={fieldName} value={value} onChange={onChange} type={type}
        required={required} disabled={disabled}
        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 transition-colors"
        placeholder={placeholder} {...rest} />
      {hint && <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>}
    </div>
  );
}

export default function TenantForm() {
  const { code: editCode } = useParams();
  const isEdit = !!editCode;
  const navigate = useNavigate();

  const [form, setForm] = useState({
    code: '', name: '', status: 'active',
    admin_email: '', admin_phone: '', admin_password: '', admin_username: 'admin',
    address: '', city: '', state: '', logo_url: '',
    max_users: 50, subscription_plan: 'standard',
    subscription_start: new Date().toISOString().split('T')[0],
    subscription_expiry: '', subscription_amount: '', billing_cycle: 'yearly', auto_renew: true, notes: '',
    db_host: 'localhost', db_port: 3306, db_name: '', db_user: 'root', db_password: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [generatingCode, setGeneratingCode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [provisionProgress, setProvisionProgress] = useState('');

  // Auto-generate code on mount for new tenants
  const fetchNewCode = useCallback(async () => {
    if (isEdit) return;
    setGeneratingCode(true);
    try {
      const res = await api.get('/tenants/generate-code');
      setForm(p => ({ ...p, code: res.data.data.code }));
    } catch { /* silently fail, user can manually enter */ }
    finally { setGeneratingCode(false); }
  }, [isEdit]);

  // Don't auto-generate on mount — let user enter code manually (refresh button available)

  useEffect(() => {
    if (isEdit) {
      setLoading(true);
      api.get(`/tenants/${editCode}`)
        .then(r => {
          const t = r.data.data;
          setForm({
            code: t.code, name: t.name, status: t.status,
            admin_email: t.admin_email || '', admin_phone: t.admin_phone || '',
            admin_password: '', admin_username: '',
            address: t.address || '', city: t.city || '', state: t.state || '', logo_url: t.logo_url || '',
            max_users: t.max_users || 50, subscription_plan: t.subscription_plan || 'standard',
            subscription_start: t.subscription_start ? t.subscription_start.split('T')[0] : '',
            subscription_expiry: t.subscription_expiry ? t.subscription_expiry.split('T')[0] : '',
            subscription_amount: t.subscription_amount ?? 0, billing_cycle: t.billing_cycle || 'yearly',
            auto_renew: t.auto_renew === 1 || t.auto_renew === true, notes: t.notes || '',
            db_host: t.db_host || 'localhost', db_port: t.db_port || 3306,
            db_name: t.db_name || '', db_user: t.db_user || 'root', db_password: '',
          });
        })
        .catch(e => setError(e.response?.data?.message || 'Failed to load'))
        .finally(() => setLoading(false));
    }
  }, [editCode, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess(''); setProvisionProgress('');

    if (!isEdit && !form.code.trim()) { setError('Organization code is required'); setSaving(false); return; }
    if (!isEdit && form.code.trim().length < 3) { setError('Organization code must be at least 3 characters'); setSaving(false); return; }
    if (!isEdit && form.code.trim().length > 25) { setError('Organization code must be at most 25 characters'); setSaving(false); return; }
    if (!form.name.trim()) { setError('Organization name is required'); setSaving(false); return; }
    if (!isEdit && !form.admin_email.trim()) { setError('Admin email is required'); setSaving(false); return; }
    if (!isEdit && !form.admin_password) { setError('Admin password is required for new tenant'); setSaving(false); return; }
    if (!isEdit && form.admin_password.length < 8) { setError('Password must be at least 8 characters'); setSaving(false); return; }

    try {
      if (isEdit) {
        const payload = { ...form };
        delete payload.code; delete payload.admin_password; delete payload.admin_username;
        if (!payload.db_password) delete payload.db_password;
        await api.put(`/tenants/${editCode}`, payload);
        setSuccess('Tenant updated successfully');
      } else {
        setProvisionProgress('Creating database and provisioning tenant...');
        const res = await api.post('/tenants', {
          code: form.code || undefined,
          name: form.name, admin_email: form.admin_email, admin_phone: form.admin_phone,
          admin_password: form.admin_password, admin_username: form.admin_username || 'admin',
          address: form.address, city: form.city, state: form.state, logo_url: form.logo_url,
          max_users: form.max_users, subscription_plan: form.subscription_plan,
          subscription_start: form.subscription_start,
          subscription_expiry: form.subscription_expiry || undefined,
          subscription_amount: form.subscription_amount || 0,
          billing_cycle: form.billing_cycle, auto_renew: form.auto_renew, notes: form.notes,
        });
        const data = res.data.data;
        setProvisionProgress('');
        setSuccess(
          `Tenant "${data.code}" created successfully!\n` +
          `Database: ${data.dbName} (${data.tablesCreated} objects)\n` +
          `Admin Login: ${data.adminUsername} / [password you entered]\n` +
          `Org Code for login: ${data.code}`
        );
        setTimeout(() => navigate('/tenants'), 4000);
      }
    } catch (e) {
      setProvisionProgress('');
      setError(e.response?.data?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center space-x-3">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
        </button>
        <div>
          <h2 className="text-xl font-bold text-gray-900">{isEdit ? 'Edit Tenant' : 'Register New Tenant'}</h2>
          <p className="text-sm text-gray-500">{isEdit ? `Editing: ${editCode}` : 'Fill in the details — database & admin account will be created automatically'}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Organization */}
        <Section title="Organization Details" icon="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" color="bg-blue-500">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Organization Code {!isEdit && <span className="text-red-400">*</span>}</label>
              <div className="flex items-center gap-2">
                <input name="code" value={form.code} onChange={handleChange} disabled={isEdit}
                  maxLength={25}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono tracking-wider focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:opacity-60"
                  placeholder="Enter unique tenant code" />
                {!isEdit && (
                  <button type="button" onClick={fetchNewCode} disabled={generatingCode}
                    className="p-2 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors disabled:opacity-50" title="Auto-generate code">
                    <svg className={`w-4 h-4 ${generatingCode ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                )}
              </div>
              {!isEdit && <p className="text-[10px] text-gray-400 mt-0.5">Enter a unique code (max 25 chars, lowercase alphanumeric) or click refresh to auto-generate</p>}
            </div>
            <InputField label="Organization Name" name="name" required placeholder="e.g., District Collectorate, Khurda" value={form.name} onChange={handleChange} />
            <InputField label="Admin Email" name="admin_email" type="email" required={!isEdit} placeholder="admin@org.gov.in" hint="Primary admin's email" value={form.admin_email} onChange={handleChange} />
            <InputField label="Admin Phone" name="admin_phone" placeholder="+91-9876543210" value={form.admin_phone} onChange={handleChange} />
            <InputField label="Address" name="address" placeholder="Full address" className="md:col-span-2" value={form.address} onChange={handleChange} />
            <InputField label="City" name="city" placeholder="City" value={form.city} onChange={handleChange} />
            <InputField label="State" name="state" placeholder="State" value={form.state} onChange={handleChange} />
          </div>
        </Section>

        {/* Admin Credentials (new only) */}
        {!isEdit && (
          <Section title="Admin Credentials" icon="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" color="bg-amber-500">
            <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 mb-4 text-xs text-amber-700">
              These credentials will be used for the new tenant's first admin login. The tenant admin can change them later.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField label="Admin Username" name="admin_username" placeholder="admin" hint="Default: admin" value={form.admin_username} onChange={handleChange} />
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Admin Password <span className="text-red-400">*</span></label>
                <div className="relative">
                  <input name="admin_password" value={form.admin_password} onChange={handleChange}
                    type={showPassword ? 'text' : 'password'} required
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 pr-10"
                    placeholder="Min 8 characters" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm">
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                <p className="text-[11px] text-gray-400 mt-0.5">Minimum 8 characters. Tenant admin can change after first login.</p>
              </div>
            </div>
          </Section>
        )}

        {/* Database (edit only) */}
        {isEdit && (
          <Section title="Database Configuration" icon="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" color="bg-green-500">
            <div className="bg-green-50 border border-green-100 rounded-lg p-3 mb-4 text-xs text-green-700">
              Database was auto-configured during creation. Only modify if migrating the database.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField label="Host" name="db_host" placeholder="localhost" value={form.db_host} onChange={handleChange} />
              <InputField label="Port" name="db_port" type="number" placeholder="3306" value={form.db_port} onChange={handleChange} />
              <InputField label="Database Name" name="db_name" disabled hint="Cannot be changed" value={form.db_name} onChange={handleChange} />
              <InputField label="User" name="db_user" placeholder="root" value={form.db_user} onChange={handleChange} />
              <InputField label="Password" name="db_password" type="password" className="md:col-span-2" placeholder="Leave blank to keep current" value={form.db_password} onChange={handleChange} />
            </div>
          </Section>
        )}

        {/* Subscription */}
        <Section title="Subscription" icon="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" color="bg-indigo-500">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Plan</label>
              <select name="subscription_plan" value={form.subscription_plan} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                <option value="basic">Basic</option><option value="standard">Standard</option><option value="premium">Premium</option>
              </select></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Billing Cycle</label>
              <select name="billing_cycle" value={form.billing_cycle} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                <option value="monthly">Monthly</option><option value="quarterly">Quarterly</option><option value="yearly">Yearly</option><option value="lifetime">Lifetime</option>
              </select></div>
            <InputField label="Amount (₹)" name="subscription_amount" type="number" placeholder="0" value={form.subscription_amount} onChange={handleChange} />
            <InputField label="Start Date" name="subscription_start" type="date" value={form.subscription_start} onChange={handleChange} />
            <InputField label="Expiry Date" name="subscription_expiry" type="date" value={form.subscription_expiry} onChange={handleChange} />
            <InputField label="Max Users" name="max_users" type="number" hint="Default: 50" value={form.max_users} onChange={handleChange} />
            <div className="flex items-center space-x-2 md:col-span-3">
              <input name="auto_renew" type="checkbox" checked={form.auto_renew} onChange={handleChange} className="w-4 h-4 text-blue-600 rounded" />
              <label className="text-sm text-gray-700">Auto Renew Subscription</label>
            </div>
          </div>
        </Section>

        {/* Additional */}
        <Section title="Additional" icon="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" color="bg-gray-500">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {isEdit && (
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select name="status" value={form.status} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                  <option value="active">Active</option><option value="suspended">Suspended</option><option value="disabled">Disabled</option>
                </select></div>
            )}
            <div className={isEdit ? '' : 'md:col-span-2'}>
              <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
              <textarea name="notes" value={form.notes} onChange={handleChange} rows={3}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="Internal notes..." />
            </div>
          </div>
        </Section>

        {/* Messages */}
        {provisionProgress && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-blue-700 text-sm flex items-center space-x-3">
            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin flex-shrink-0" />
            <span className="font-medium">{provisionProgress}</span>
          </div>
        )}
        {error && <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm"><strong>Error:</strong> {error}</div>}
        {success && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-green-700 text-sm">
            <div className="font-semibold mb-1">Success!</div>
            <pre className="whitespace-pre-wrap text-xs font-mono bg-green-100/50 rounded p-2 mt-1">{success}</pre>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-2 pb-8">
          <button type="button" onClick={() => navigate(-1)} className="px-4 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl text-sm transition-colors">Cancel</button>
          <button type="submit" disabled={saving}
            className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium shadow-sm disabled:opacity-50 transition-colors flex items-center space-x-2">
            {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
            <span>{saving ? (isEdit ? 'Updating...' : 'Provisioning...') : isEdit ? 'Update Tenant' : 'Create & Provision Tenant'}</span>
          </button>
        </div>
      </form>
    </div>
  );
}
